import React from 'react';
import {
  BuildingStorefrontIcon,
  HomeModernIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import {
  FULFILLMENT_MODES,
  type FulfillmentMode,
  type FulfillmentProviderReadModel,
  getFulfillmentModeLabel,
  resolveServiceFulfillmentCapabilities,
} from '../../shared/fulfillment';

type ServiceFulfillmentSummaryVariant = 'profile' | 'popup';

interface ServiceFulfillmentSummaryProps {
  provider?: FulfillmentProviderReadModel | null;
  variant?: ServiceFulfillmentSummaryVariant;
  className?: string;
}

const modeIcons: Record<FulfillmentMode, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  customerDropoff: BuildingStorefrontIcon,
  servicePickup: TruckIcon,
  onsiteService: HomeModernIcon,
};

const popupModeLabels: Record<FulfillmentMode, string> = {
  customerDropoff: 'Do serwisu',
  servicePickup: 'Odbiór',
  onsiteService: 'U klienta',
};

const fallbackLabel = 'Szczegóły realizacji ustalane indywidualnie';

const ServiceFulfillmentSummary: React.FC<ServiceFulfillmentSummaryProps> = ({
  provider,
  variant = 'profile',
  className = '',
}) => {
  const capabilities = resolveServiceFulfillmentCapabilities(provider);
  const shouldShowConfiguredModes =
    capabilities.source === 'providerProfile' && capabilities.state === 'available';
  const enabledModes = shouldShowConfiguredModes ? capabilities.enabledModes : [];

  if (variant === 'popup') {
    return (
      <div
        className={`flex min-w-0 flex-wrap items-center gap-1.5 text-[11px] leading-tight text-primary-light-300 ${className}`}
        data-testid="service-fulfillment-summary"
      >
        {enabledModes.length > 0 ? (
          enabledModes.map((mode) => {
            const Icon = modeIcons[mode];
            return (
              <span
                key={mode}
                className="inline-flex max-w-full items-center gap-1 px-0.5 py-1"
                title={getFulfillmentModeLabel(mode)}
              >
                <Icon className="h-3.5 w-3.5 shrink-0 text-ratuj-300" aria-hidden="true" />
                <span className="truncate">{popupModeLabels[mode]}</span>
              </span>
            );
          })
        ) : (
          <span className="inline-flex min-w-0 items-center gap-1 text-primary-light-400">
            <WrenchScrewdriverIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="truncate">{fallbackLabel}</span>
          </span>
        )}
      </div>
    );
  }

  return (
    <section className={`space-y-3 ${className}`} data-testid="service-fulfillment-summary">
      <h2 className="flex items-center gap-2 text-base font-semibold text-primary-light-100 sm:text-lg">
        <WrenchScrewdriverIcon className="h-5 w-5 text-ratuj-300" aria-hidden="true" />
        Jak możesz zlecić naprawę
      </h2>

      {enabledModes.length > 0 ? (
        <div className="rs-soft-row-group overflow-hidden">
          {FULFILLMENT_MODES.filter((mode) => enabledModes.includes(mode)).map((mode) => {
            const Icon = modeIcons[mode];
            return (
              <div key={mode} className="rs-soft-row flex min-w-0 items-center gap-3 px-3 py-2.5">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-ratuj-400/10 text-ratuj-300">
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <span
                  className="min-w-0 text-sm font-medium leading-snug text-primary-light-200 [overflow-wrap:anywhere]"
                  title={getFulfillmentModeLabel(mode)}
                >
                  {getFulfillmentModeLabel(mode)}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rs-profile-content-layer px-3 py-2.5">
          <p className="text-sm leading-relaxed text-primary-light-300">{fallbackLabel}.</p>
        </div>
      )}
    </section>
  );
};

export default ServiceFulfillmentSummary;
